/**
 * tableAvailability.js
 *
 * Shared logic for computing "when is each table next available?"
 * Used by the portal (TablesView/WaitlistPanel) and should mirror
 * the logic used in the POS app's Waitlist hold modal.
 *
 * Accounts for:
 *   - Reservations, respecting customDurationMinutes per reservation
 *   - tableBlocks (manually blocked windows on the restaurant doc)
 *   - Terminal/canceled reservation statuses (excluded from calculations)
 *   - Today's date only — historical reservations are ignored
 *   - Results rounded up to the nearest 15-minute interval
 */

export const TERMINAL_STATUSES = new Set([
  'canceled', 'cancelled', 'walk_out', 'comped', 'voided', 'noShow', 'completed', 'fullyPaid',
]);

/**
 * Parse a reservation's date+time fields into a millisecond timestamp.
 * Handles Firestore Timestamps, 24-hour "HH:MM" strings, and 12-hour "HH:MM AM/PM" strings.
 */
function parseResStartMs(r) {
  try {
    const rDate = r.date?.toDate
      ? r.date.toDate()
      : r.date?.toMillis
      ? new Date(r.date.toMillis())
      : new Date(r.date);
    const parts = r.time.split(/[: ]/);
    let h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const ampm = parts[2]?.toUpperCase();
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return new Date(rDate.getFullYear(), rDate.getMonth(), rDate.getDate(), h, m).getTime();
  } catch {
    return null;
  }
}

/** Round a millisecond timestamp up to the nearest 15-minute boundary. */
function roundUp15(ms) {
  const interval = 15 * 60_000;
  return Math.ceil(ms / interval) * interval;
}

/** Format a millisecond timestamp as a 12-hour time string, e.g. "7:15 PM". */
export function fmt12FromMs(ms) {
  const d = new Date(ms);
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
}

/**
 * Compute when a single table is next available from `nowMs`.
 *
 * @param {string|number} tableId
 * @param {Array}  rawReservations    - Raw Firestore reservation docs (not pre-filtered)
 * @param {Object} tableBlocks        - restaurantDoc.tableBlocks: { [tableId]: { startsAt, expiresAt } }
 * @param {number} nowMs              - Current time in milliseconds
 * @param {number} defaultLengthMin   - Restaurant's default reservation length in minutes
 * @returns {{ nextAvailableMs: number, isNow: boolean }}
 */
export function getTableNextAvailable(tableId, rawReservations, tableBlocks, nowMs, defaultLengthMin) {
  const tid = String(tableId);

  const todayStart = new Date(nowMs); todayStart.setHours(0, 0, 0, 0);
  const todayEnd   = new Date(nowMs); todayEnd.setHours(23, 59, 59, 999);

  const busyWindows = [];

  // ── Reservations ────────────────────────────────────────────────────────────
  (rawReservations || []).forEach(r => {
    if (r.canceledAt || TERMINAL_STATUSES.has(r.status)) return;
    const ids = r.tableIds ? r.tableIds.map(String) : r.tableId ? [String(r.tableId)] : [];
    if (!ids.includes(tid)) return;
    const startMs = parseResStartMs(r);
    if (!startMs || startMs < todayStart.getTime() || startMs > todayEnd.getTime()) return;
    const durationMs = ((r.customDurationMinutes ?? defaultLengthMin) || 60) * 60_000;
    busyWindows.push({ start: startMs, end: startMs + durationMs });
  });

  // ── tableBlocks ─────────────────────────────────────────────────────────────
  // tableBlocks keys may be stored as the raw tableId value
  const block = tableBlocks?.[tid] ?? tableBlocks?.[tableId];
  if (block) {
    const blockStart = block.startsAt ? new Date(block.startsAt).getTime() : todayStart.getTime();
    const blockEnd   = block.expiresAt ? new Date(block.expiresAt).getTime() : todayEnd.getTime();
    // Only include if this block overlaps with today
    if (blockEnd > todayStart.getTime() && blockStart < todayEnd.getTime()) {
      busyWindows.push({
        start: Math.max(blockStart, nowMs),
        end:   blockEnd,
      });
    }
  }

  // Sort windows by start time, then walk cursor forward
  busyWindows.sort((a, b) => a.start - b.start);

  let cursor = nowMs;
  for (const w of busyWindows) {
    if (cursor < w.start) break;   // free gap — table is available at cursor
    if (cursor < w.end) cursor = w.end;
  }

  const isNow = cursor <= nowMs;
  return {
    nextAvailableMs: isNow ? nowMs : roundUp15(cursor),
    isNow,
  };
}

/**
 * Compute next-available time for every table and return them grouped by slot,
 * sorted soonest first. "Available Now" is always the first group when present.
 *
 * @param {Array}  tables
 * @param {Array}  rawReservations
 * @param {Object} tableBlocks
 * @param {number} nowMs
 * @param {number} defaultLengthMin
 * @returns {Array<{ label: string, ms: number, tables: Array }>}
 */
export function getTableAvailabilityGroups(tables, rawReservations, tableBlocks, nowMs, defaultLengthMin) {
  const groupMap = {};

  (tables || []).forEach(t => {
    const { nextAvailableMs, isNow } = getTableNextAvailable(
      t.tableId, rawReservations, tableBlocks, nowMs, defaultLengthMin,
    );
    const key = isNow ? 'now' : String(nextAvailableMs);
    if (!groupMap[key]) {
      groupMap[key] = {
        label: isNow ? 'Available Now' : fmt12FromMs(nextAvailableMs),
        ms:    isNow ? 0 : nextAvailableMs,
        tables: [],
      };
    }
    groupMap[key].tables.push(t);
  });

  return Object.values(groupMap).sort((a, b) => a.ms - b.ms);
}
